import { useLocation } from 'react-router-dom';
import { Phone, Envelope, MapPin, CalendarCheck } from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';

/**
 * ScheduleSection — Contact form section above the Footer on every public page.
 * Skipped on /booking since that page already has the GHL form in its main content.
 */
export const ScheduleSection = () => {
  const { pathname } = useLocation();
  if (pathname === '/booking') return null;
  const ctaUrl = BRAND.ghlBookingUrl || '/booking';
  const ctaExternal = ctaUrl.startsWith('http');

  return (
    <section
      data-testid="schedule-section"
      className="bg-[#FBF8F1] py-20 sm:py-24 border-t border-[#E8E2D5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* GHL CTA strip */}
        <div className="mb-14 flex flex-col sm:flex-row items-center justify-between gap-5 bg-[#800000] text-white px-6 sm:px-10 py-6 rounded-sm shadow-lg">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-bold mb-1">
              Skip the form
            </p>
            <p className="font-serif-display text-xl sm:text-2xl leading-tight">
              Book directly on India's <span className="italic gold-shimmer">live calendar</span>.
            </p>
          </div>
          <a
            href={ctaUrl}
            data-testid="ghl-cta-button"
            {...(ctaExternal
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-md hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap"
          >
            <CalendarCheck weight="bold" size={18} />
            Schedule Now
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — contact info */}
          <div className="lg:col-span-5">
            <p className="flex items-center gap-3 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-6">
              <span className="h-px w-8 bg-[#D4AF37]" />
              Get in Touch
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A0000] leading-[1.05] mb-7">
              Ready to Schedule <span className="italic">Your Notary?</span>
            </h2>
            <p className="text-[#5C5C5C] text-base leading-relaxed mb-10 max-w-md">
              Fill out the form and India will confirm your appointment and come to you.
              24 / 7 same-day availability for emergencies.
            </p>

            <ul className="space-y-7">
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000] flex items-center justify-center flex-shrink-0 rounded-sm">
                  <Phone weight="fill" size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#800000] font-bold mb-1">
                    Phone
                  </p>
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    data-testid="schedule-phone-link"
                    className="text-[#1A0000] font-medium text-lg hover:text-[#D4AF37] transition-colors"
                  >
                    {BRAND.phone}
                  </a>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000] flex items-center justify-center flex-shrink-0 rounded-sm">
                  <Envelope weight="fill" size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#800000] font-bold mb-1">
                    Email
                  </p>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="schedule-email-link"
                    className="text-[#1A0000] font-medium text-base hover:text-[#D4AF37] transition-colors break-all"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000] flex items-center justify-center flex-shrink-0 rounded-sm">
                  <MapPin weight="fill" size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#800000] font-bold mb-1">
                    Location
                  </p>
                  <p className="text-[#1A0000] font-medium">Nashville, Tennessee</p>
                  <p className="text-[#5C5C5C] text-xs mt-0.5">
                    Mobile across Middle TN · RON worldwide
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Right — GHL embedded contact form */}
          <div className="lg:col-span-7">
            <div
              data-testid="ghl-contact-form-wrapper"
              className="bg-white shadow-2xl rounded-sm border-t-4 border-[#D4AF37] overflow-hidden"
            >
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/36gZoJkMhocEE5hHdL78"
                style={{ width: '100%', height: '485px', border: 'none', borderRadius: '3px' }}
                id="inline-36gZoJkMhocEE5hHdL78"
                data-layout='{"id":"INLINE"}'
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Contact Form"
                data-height="485"
                data-layout-iframe-id="inline-36gZoJkMhocEE5hHdL78"
                data-form-id="36gZoJkMhocEE5hHdL78"
                title="Contact Form"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
